import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiRequest, getToken, setToken } from "./api";

export type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
};

export type BusinessSummary = {
  id: string;
  name: string;
  slug: string;
  industry: string;
  description?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  heroHeadline?: string | null;
  callToAction?: string | null;
  aboutContent?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  timezone?: string | null;
  locale?: string | null;
  currencyCode?: string | null;
  brandTone?: string | null;
  paymentCollectionMode?: string | null;
  website?: { status?: string; themeId?: string } | null;
  subscription?: {
    planCode?: string;
    status?: string;
    aiTokensLimit?: number;
  } | null;
  _count?: { clients?: number; bookings?: number; services?: number };
};

type Membership = {
  id: string;
  role: string;
  business: BusinessSummary;
};

type AuthContextValue = {
  ready: boolean;
  token: string | null;
  user: AuthUser | null;
  memberships: Membership[];
  business: BusinessSummary | null;
  businessId: string | null;
  setSession: (token: string, user: AuthUser) => Promise<void>;
  refreshBusinesses: () => Promise<void>;
  selectBusiness: (businessId: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const BUSINESS_KEY = "duts_saas_business_id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [token, setTokenState] = useState<string | null>(getToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(
    localStorage.getItem(BUSINESS_KEY),
  );

  const refreshBusinesses = useCallback(async () => {
    const data = await apiRequest<{ memberships: Membership[] }>(
      "/businesses/mine",
    );
    setMemberships(data.memberships);
    const stored = localStorage.getItem(BUSINESS_KEY);
    const nextId =
      data.memberships.find((m) => m.business.id === stored)?.business.id ??
      data.memberships[0]?.business.id ??
      null;
    setBusinessId(nextId);
    if (nextId) localStorage.setItem(BUSINESS_KEY, nextId);
  }, []);

  const setSession = useCallback(
    async (nextToken: string, nextUser: AuthUser) => {
      setToken(nextToken);
      setTokenState(nextToken);
      setUser(nextUser);
      try {
        await refreshBusinesses();
      } catch {
        setMemberships([]);
      }
    },
    [refreshBusinesses],
  );

  const logout = useCallback(() => {
    setToken(null);
    setTokenState(null);
    setUser(null);
    setMemberships([]);
    setBusinessId(null);
    localStorage.removeItem(BUSINESS_KEY);
  }, []);

  const selectBusiness = useCallback((id: string) => {
    setBusinessId(id);
    localStorage.setItem(BUSINESS_KEY, id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const existing = getToken();
      if (!existing) {
        if (!cancelled) setReady(true);
        return;
      }

      try {
        const me = await apiRequest<{ user: AuthUser }>("/auth/me");
        if (cancelled) return;
        setTokenState(existing);
        setUser(me.user);
        try {
          await refreshBusinesses();
        } catch {
          setMemberships([]);
        }
      } catch {
        setToken(null);
        if (!cancelled) {
          setTokenState(null);
          setUser(null);
          setMemberships([]);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [refreshBusinesses]);

  const business = useMemo(
    () =>
      memberships.find((m) => m.business.id === businessId)?.business ??
      memberships[0]?.business ??
      null,
    [memberships, businessId],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ready,
      token,
      user,
      memberships,
      business,
      businessId: business?.id ?? null,
      setSession,
      refreshBusinesses,
      selectBusiness,
      logout,
    }),
    [
      ready,
      token,
      user,
      memberships,
      business,
      setSession,
      refreshBusinesses,
      selectBusiness,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
